import React, { CSSProperties, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import MediaRow from '../common/MediaRow'
import { brandColor as DEFAULT_BRAND_COLOR } from '../style/brandConfig'
import { selectBrandColor } from '../store/base/commonSlice'
import { MediaRowFieldsFragment, usePageQuery } from '../graphql'
import LoadingScreen from '../common/LoadingScreen'
import { useSelector } from 'react-redux'
import { selectWindowSize } from '../store/base/commonSlice'
import { WindowSize } from '../constants/enums'
import { useLazyLoadMediaRows } from '../utils/hooks/useLazyLoadMediaRows'
import LoadingIndicator from '../common/LoadingIndicator'
import { deduplicate } from '../utils/deduplicate'
import { getFilenameFromUrl, reconstructUrl } from '../utils/encodeFileName'

const CustomPage = () => {
  const take = 5

  const windowSize = useSelector(selectWindowSize)

  const params = useParams()
  const navigate = useNavigate()

  const {
    data,
    loading: initLoading,
    fetchMore,
  } = usePageQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      data: {
        slug: params?.pageSlug,
      },
      rowsTake: take,
      rowsSkip: 0,
    },
  })

  if (!initLoading && !data) {
    navigate('/page-not-found')
  }

  let item = data?.page

  const currentBrandColor = useSelector(selectBrandColor) || DEFAULT_BRAND_COLOR

  useEffect(() => {
    if (item) {
      const backdrop = document?.querySelector('.bottomLayer') as HTMLElement
      if (item?.bannerImage?.src && backdrop) {
        backdrop.style.backgroundColor = '#0D0D0D'
      }
      return () => {
        if (backdrop) {
          backdrop.style.backgroundColor = currentBrandColor
        }
      }
    }
  }, [item, currentBrandColor])

  const backgroundImageStyle: React.CSSProperties =
    windowSize === WindowSize.Mobile ? { ...styles.backgroundImageMobile } : { ...styles.backgroundImage }

  if (item?.bannerImage) {
    let encodedUrl: string | null | undefined = item?.bannerImage?.src
    let filename: string | null | undefined = getFilenameFromUrl(item?.bannerImage?.src)
    if (filename) {
      try {
        // encodeURIComponent occasionally causing some weirdness with double encoding, so decoding first...
        filename = decodeURIComponent(filename)
        filename = encodeURIComponent(filename)
        // encode doesn't handle apostrophes, this is primary reason for these changes, above is just extra handling
        filename = filename?.replace(/'/g, '%27')
        encodedUrl = reconstructUrl(item?.bannerImage?.src, filename)
      } catch (e) {
        console.error(e)
      }
    }
    backgroundImageStyle.background = `no-repeat center/cover url('${encodedUrl}')`
    delete backgroundImageStyle.backgroundColor
  }

  const fadeImage: CSSProperties =
    windowSize === WindowSize.Mobile
      ? {
          top: 40,
          left: 0,
          right: 0,
          height: 110,
          position: 'absolute',
          width: '100vw',
          zIndex: 1,
          background:
            'transparent linear-gradient(180deg, #000000 0%, #00000000 0%, #0D0D0D 85%, #0D0D0D 100%, #545454 100%) 0% 0% no-repeat padding-box',
        }
      : {
          display: 'none',
        }

  const gradientBottom: CSSProperties =
    windowSize === WindowSize.Mobile
      ? {
          top: 75,
          left: 0,
          right: 0,
          height: 525,
          position: 'absolute',
          width: '100vw',
          zIndex: 1,
          background:
            'transparent linear-gradient(0deg, #0D0D0D 0%, #0D0D0DBF 0%, #0D0D0DBE 64%, #0D0D0D00 100%) 0% 0% no-repeat padding-box',
        }
      : {
          top: 174,
          left: 0,
          right: 0,
          height: 1493,
          position: 'absolute',
          width: '100vw',
          zIndex: 1,
          background: 'transparent linear-gradient(0deg, #0D0D0D 72%, #0D0D0D00 100%) 0% 0% no-repeat padding-box',
        }

  const scrollBackground: CSSProperties =
    windowSize === WindowSize.Mobile
      ? {
          top: 150,
          left: 0,
          right: 0,
          height: 'calc(100vh + 100%)',
          bottom: '-10vh',
          position: 'absolute',
          width: '100%',
          zIndex: 1,
          background: '#0D0D0D',
        }
      : {
          top: 174 + 1493,
          left: 0,
          right: 0,
          height: 'calc(100vh + 100%)',
          bottom: '-10vh',
          position: 'absolute',
          width: '100vw',
          zIndex: 1,
          background: '#0D0D0D',
        }

  const topGradient: CSSProperties =
    windowSize === WindowSize.Mobile
      ? {
          top: 0,
          left: 0,
          right: 0,
          height: 110,
          position: 'fixed',
          width: '100vw',
          zIndex: 2,
          background: 'transparent linear-gradient(180deg, #0D0D0DBF 0%, #0D0D0D00 100%) 0% 0% no-repeat padding-box',
        }
      : {
          top: 0,
          left: 0,
          right: 0,
          height: 348,
          position: 'fixed',
          width: '100vw',
          zIndex: 2,
          background: 'transparent linear-gradient(180deg, #0D0D0DBF 0%, #0D0D0D00 100%) 0% 0% no-repeat padding-box',
        }

  const filteredRows = useMemo(() => {
    const dedupeRows = deduplicate<MediaRowFieldsFragment>(data?.page?.rows || [], (row) => row.id)
    return dedupeRows.filter((row) => row.items.list.length > 0) || []
  }, [data?.page?.rows])

  const { inViewRef, loadingMore } = useLazyLoadMediaRows('page', data?.page?.rows || [], fetchMore, initLoading, take)

  return (
    <LoadingScreen loading={initLoading && !data}>
      <div style={topGradient} />
      <div style={fadeImage} />
      <div style={gradientBottom} />
      <div style={scrollBackground} />
      <div className={'customWrapper'} style={styles.container}>
        <div style={{ height: '100%' }}>
          <div style={backgroundImageStyle}></div>
        </div>
        <div style={windowSize === WindowSize.Mobile ? styles.headerMobile : styles.header}>{item?.name}</div>
        <div style={{ zIndex: 2, marginTop: windowSize === WindowSize.Mobile ? 50 : 0 }}>
          {filteredRows.map((row, index) => {
            const isLastRow = index === filteredRows.length - 1
            return (
              <div key={index} ref={isLastRow ? inViewRef : null}>
                <MediaRow key={index} {...row} />
              </div>
            )
          })}
          <div style={{ height: 50, marginBottom: 25, marginTop: -25 }}>{loadingMore && <LoadingIndicator />}</div>
        </div>
      </div>
    </LoadingScreen>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  } as React.CSSProperties,
  backgroundImage: {
    height: '100%',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    width: '100%',
    position: 'fixed',
    backgroundColor: '#0D0D0D',
  } as React.CSSProperties,
  backgroundImageMobile: {
    height: 150,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    width: '100%',
    position: 'fixed',
  } as React.CSSProperties,
  exploreBackgroundOverlayTop: {
    position: 'absolute',
    top: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(to top, transparent 50%, #0D0D0D 100%)',
    zIndex: 1,
  } as React.CSSProperties,
  exploreBackgroundOverlayCover: {
    position: 'absolute',
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#0D0D0D',
    transition: 'all .3s ease-in-out',
    zIndex: 1,
  } as React.CSSProperties,
  exploreBackgroundOverlayBottom: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(to bottom, transparent 12%, #0D0D0D 75%)',
    zIndex: 1,
  } as React.CSSProperties,
  header: {
    color: 'white',
    marginTop: 300,
    fontWeight: 700,
    fontSize: 64,
    width: '100%',
    zIndex: 1,
    opacity: 1,
    cursor: 'hover',
  } as React.CSSProperties,
  headerMobile: {
    color: 'white',
    fontWeight: 700,
    fontSize: 28,
    backgroundColor: 'transparent',
    zIndex: 1,
    marginTop: 100,
    marginLeft: 35,
    marginRight: 35,
    opacity: 1,
    cursor: 'hover',
  } as React.CSSProperties,
}

export default CustomPage
